import * as React from "react";
import { Link as RouterLink, type LinkProps } from "react-router";
import { useLocalizedPath } from "~/lib/use-locale-preference";

// The one place internal navigation goes through. Wraps react-router's Link and
// auto-prefixes `to` with the current page's locale (see useLocalizedPath) when a
// localized route for it exists — every caller just links to the canonical English
// path like "/products/drive" and gets this for free, with nothing to remember.
//
// Only string `to` values are localized — react-router also accepts a Partial<Path>
// object, which every caller in this app happens not to use for internal nav; if one
// ever needs to, extend useLocalizedPath to accept that shape too.
//
// eslint.config.js's no-restricted-imports rule blocks importing Link from
// "react-router" directly anywhere except this file, so this is the only place that
// can get out of sync with it. LocaleDialog.tsx is the one deliberate exception — it
// IS the language switcher, so it needs react-router's raw Link to swap locales via
// localizedPath(pathname, targetPath) instead of staying in the current one.
export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(function Link({ to, ...props }, ref) {
  const isStringTo = typeof to === "string";
  // Hooks can't be called conditionally — "" is a throwaway input for the non-string
  // case below, where its result is discarded in favor of the original `to` object.
  const localizedTo = useLocalizedPath(isStringTo ? to : "");
  return <RouterLink ref={ref} to={isStringTo ? localizedTo : to} {...props} />;
});
